import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import i18next, { TFunction } from "i18next";
import { IssueData, z } from "zod";

type E = Record<string, any>;

/**
 * valueBelongsToEnum verifies if the given value, which can be any, belongs to the
 * input enumeration (enumObject).
 *
 * @param enumObject the enumeration object
 * @param value      the target value
 * @returns          true if value is a member of enumObject
 */
const valueBelongsToEnum = <T extends E>(enumObject: T, value: any): value is T[keyof T] => {
	return Object.values(enumObject).includes(value as T[keyof T]);
};

/**
 * valueToEnum returns value as a member of enumObject or the first member of enumObject
 * if value is not a valid member of that enumeration.
 *
 * @param enumObject the enumeration object
 * @param value      the target value
 * @returns          value as a member of enumObject or the first member of enumObject
 */
export const valueToEnum = <T extends E>(enumObject: T, value: any): T[keyof T] => {
	return (valueBelongsToEnum(enumObject, value) ? value : Object.values(enumObject)[0]) as T[keyof T];
};

/**
 * Returns a numeric formatter.
 *
 * @param style
 * @param minimumFractionDigits
 * @param maximumFractionDigits
 */
export const numericFormatter = (
	style: "decimal" | "unit" | "percent" | "currency" = "decimal",
	minimumFractionDigits?: number,
	maximumFractionDigits?: number
): Intl.NumberFormat => {
	return Intl.NumberFormat(i18next.language, { minimumFractionDigits, maximumFractionDigits, style });
};

/**
 * Formats a numeric value.
 *
 * @param value
 * @param style
 * @param minimumFractionDigits
 * @param maximumFractionDigits
 */
export const formatNumber = (
	value: number,
	style: "decimal" | "unit" | "percent" | "currency" = "decimal",
	minimumFractionDigits?: number,
	maximumFractionDigits?: number
): string => {
	return numericFormatter(style, minimumFractionDigits, maximumFractionDigits).format(value);
};

export const compactNumberFormatter = (minimumFractionDigits = 0, maximumFractionDigits = 1): Intl.NumberFormat => {
	return Intl.NumberFormat(i18next.language, {
		minimumFractionDigits,
		maximumFractionDigits,
		notation: "compact",
	});
};

export const formatCompactNumber = (value: number, minimumFractionDigits = 0, maximumFractionDigits = 1): string => {
	return compactNumberFormatter(minimumFractionDigits, maximumFractionDigits).format(value);
};

/**
 * Returns true if arg i is T.
 * @param i the input value
 * @returns true if arg i is T
 */
export const isType = <T>(i: any): i is T => true;

/**
 * Obtains an element and allows users to download its graphical representation in .png or .jpg formats
 *
 * @param ref      the element reference
 * @param fileName the image file name
 * @param format   the image format ("jpg", "png")
 */
export const downloadElementAsImage = async (
	ref: React.RefObject<HTMLElement | null>,
	fileName: string,
	format: "png" | "jpg"
) => {
	const element = ref?.current;

	if (!ref || !element) {
		console.error(`cannot convert element into image: null reference`);
		return;
	}

	try {
		const canvas = await html2canvas(element, {
			useCORS: true,
			scale: 1,
		});

		const link = document.createElement("a");
		link.href = canvas.toDataURL(`image/${format}`);
		link.download = `${fileName}.${format}`;
		link.click();
	} catch (e) {
		console.error(`cannot convert element into image:`, e);
	}
};

export const useObserveSize = <T extends HTMLElement>() => {
	const ref = useRef<T | null>(null);
	const [size, setSize] = useState({ width: 0, height: 0 });

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const observer = new ResizeObserver(([entry]) => {
			const { width, height } = entry.contentRect;
			setSize({ width, height });
		});

		observer.observe(ref.current);

		return () => observer.disconnect();
	}, [ref]);

	return { ref, size };
};

export const useObservePadding = <T extends HTMLElement>() => {
	const ref = useRef<T | null>(null);
	const [padding, setPadding] = useState({ top: 0, right: 0, bottom: 0, left: 0 });

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const computed = getComputedStyle(ref.current);

		setPadding({
			top: parseFloat(computed.paddingTop || "0"),
			right: parseFloat(computed.paddingRight || "0"),
			bottom: parseFloat(computed.paddingBottom || "0"),
			left: parseFloat(computed.paddingLeft || "0"),
		});
	}, [ref]);

	return { ref, padding };
};

/**
 * Accepts a password and its confirmation field and validates them.
 * This function expects to be used in a zod validation scheme as a super refinement function where
 * "password" and "passwordConfirm" are fields available at paths named "password" and "passwordConfirm".
 *
 * @param password
 * @param passwordConfirm
 * @param t
 */
export const validatePassword = (password: string, passwordConfirm: string, t: TFunction): IssueData[] => {
	const issues: IssueData[] = [];

	if (password && passwordConfirm && password !== "" && password !== passwordConfirm) {
		issues.push({
			path: ["passwordConfirm"],
			code: z.ZodIssueCode.custom,
			message: t("password/must be confirmed"),
		});
	}

	if (!password || password === "") {
		return issues;
	}

	if (!password.match(/\d+/)) {
		issues.push({
			path: ["password"],
			code: z.ZodIssueCode.custom,
			message: t("password/must contain digit"),
		});
	}

	if (!password.match(/\W+/)) {
		issues.push({
			path: ["password"],
			code: z.ZodIssueCode.custom,
			message: t("password/must contain non-word"),
		});
	}

	if (!password.match(/[a-zA-Z]+/)) {
		issues.push({
			path: ["password"],
			code: z.ZodIssueCode.custom,
			message: t("password/must contain alpha"),
		});
	}

	if (password.length < 12 || password.length > 255) {
		issues.push({
			path: ["password"],
			code: z.ZodIssueCode.custom,
			message: t("password/length not valid"),
		});
	}

	if (password !== passwordConfirm) {
		issues.push({
			path: ["passwordConfirm"],
			code: z.ZodIssueCode.custom,
			message: t("password/must be confirmed"),
		});
	}

	return issues;
};

/**
 * Converts a hexadecimal color into an object containing the
 * corresponding h,s,l values
 * @param hex
 * @returns
 */
export function hexToCssHsl(hex: string) {
	let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex) || ["0", "0", "0"];
	let r = parseInt(result[1], 16) / 255;
	let g = parseInt(result[2], 16) / 255;
	let b = parseInt(result[3], 16) / 255;
	let max = Math.max(r, g, b),
		min = Math.min(r, g, b);
	let h = (max + min) / 2;
	let s = (max + min) / 2;
	let l = (max + min) / 2;
	if (max === min) {
		h = s = 0; // achromatic
	} else {
		let d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	h = Math.round(h * 360);
	s = Math.round(s * 100);
	l = Math.round(l * 100);

	return { h, s, l };
}

// Commonly used sizes for grid items in a 12-column layout
export const col4 = { xs: 12, md: 4 };
export const col6 = { xs: 12, md: 6 };
export const col3 = { xs: 6, md: 3 };

/**
 * Converts a 2-letter country code to its flag emoji
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "IT", "FR", "DE")
 * @returns Flag emoji for the given country code
 */
export const getCountryFlag = (countryCode: string): string => {
	if (!countryCode || countryCode.length !== 2) {
		return "";
	}

	const codePoints = countryCode
		.toUpperCase()
		.split("")
		.map((char) => 127397 + char.charCodeAt(0));

	return String.fromCodePoint(...codePoints);
};
